import { toast } from '@arionbanki/arionui';
import { PUBLIC_URL } from 'consts/api';
import { downloadPDF } from 'helpers';
import isEmpty from 'lodash/isEmpty';
import { AppContext } from 'providers/AppContext';
import { useContext, useEffect, useState } from 'react';
import { LoadingAction } from 'store/actions';
import { LoadingState } from 'store/state';
import { getInfoboxMessageForStatus, getSigningIdInLoop, startSigning, submitRedirect } from './SignHelpers';

type SigningResponse = {
	processInfo: {
		signingId: string | null;
		hasSigningAuthority?: boolean;
	};
};

export function useSign() {
	const ctx = useContext(AppContext);
	const { config, dispatch, entityType, flowType, http, state, fetchRelations, t } = ctx;
	const { API, API_DIGITAL_SIGNATURE_URL, API_URL, IS_EMPLOYEE_WEB } = config;
	const { agreementProcessId = '', processStatus = 0 } = state.context.processInfo || {};
	const { token } = state.user;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [signedModalMessage, setSignedModalMessage] = useState('');
	const [isSubmited, setIsSubmited] = useState(false);

	if (agreementProcessId === '' || processStatus === 0) {
		console.error('There is no agreement process to sign');
	}

	const startSigningProcess = async () => {
		try {
			const msg = t('document_signing_submitting_label', 'document_signing');
			await startSigning(http, agreementProcessId, API.sign, msg);

			const res = await getSigningIdInLoop<SigningResponse>(http, `${API.sign}/${agreementProcessId}`);
			if (isEmpty(res?.processInfo?.signingId)) {
				toast.error(t('document_signing_no_signing_id', 'document_signing'), t('default_api_error'));
				throw new Error('There is no signingID');
			}

			if (true === res?.processInfo?.hasSigningAuthority) {
				const publicUrl = PUBLIC_URL ? `${PUBLIC_URL}/` : '';
				const destinationUrl = `${globalThis.location.origin}/${publicUrl}${flowType}/${entityType}/finish`;
				const returnUrl = `${API_URL}/api/v1/redirect?destinationUrl=${destinationUrl}`;
				const eSignatureURL = `${API_DIGITAL_SIGNATURE_URL}/${API.external.eSignatureRedirect}`;
				const payload = { docid: `${res.processInfo.signingId}`, returnUrl, token };
				return submitRedirect(eSignatureURL, payload);
			}

			toast.error(t('document_signing_no_signing_authority', 'document_signing'), t('default_api_error'));
			throw new Error('No signing authority');
		} catch (ex) {
			console.error(ex);
			toast.error(t('api_error'), '');
			throw ex;
		}
	};

	const handlePrint = () => {
		const loading = LoadingState(1, t('downloading_the_document', 'document_signing'));
		dispatch([LoadingAction, { loading }]);
		downloadPDF(
			`${API.contractToBeSigned}/${agreementProcessId}`,
			`${agreementProcessId}.pdf`,
			'POST',
			token,
			() => {
				dispatch([LoadingAction, { loading: LoadingState(-1) }]);
				fetchRelations();
			}
		);
	};

	const handleSend = async () => {
		setIsSubmited(true);
		const msg = t('document_signing_submitting_label', 'document_signing');
		await startSigning(http, agreementProcessId, API.sign, msg);
		await fetchRelations();
		setSignedModalMessage(t('document_has_been_signed', 'document_signing'));
	};

	const handleConfirm = async () => {
		setIsSubmited(true);
		setIsConfirmModalOpen(false);
		const msg = t('one_moment', 'document_signing');
		await startSigning(http, agreementProcessId, API.sign, msg, true);
		await fetchRelations(true);
	};

	useEffect(() => {
		fetchRelations().then(() => {
			if (!IS_EMPLOYEE_WEB) {
				dispatch([LoadingAction, { loading: LoadingState(1) }]);
				startSigningProcess();
			}
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		...ctx,
		handleConfirm,
		handlePrint,
		handleSend,
		infoboxMessage: t(getInfoboxMessageForStatus(processStatus)),
		isConfirmModalOpen,
		isSubmited,
		setIsConfirmModalOpen,
		setSignedModalMessage,
		signedModalMessage
	};
}
